import React, { useState, useEffect } from 'react';
import { Document } from '../../lib/db';
import { FileText, Download, Loader, AlertCircle, Mail, Calendar, User, Users, Paperclip } from 'lucide-react';

interface DocumentPreviewProps {
  document: Document;
  onFileAttachment?: (attachment: any) => void;
}

interface EmailData {
  subject: string;
  from: string;
  to: string;
  cc?: string;
  date: string;
  body?: string; // Legacy field
  bodyText?: string;
  bodyHtml?: string;
  hasFormatting?: boolean;
  attachments: Array<{
    filename: string;
    size: number;
    contentType?: string;
    encoding?: string;
    data?: string;
    msgFile?: boolean;
  }>;
}

interface WordData {
  html: string;
  text: string;
  messages: Array<{
    type: string;
    message: string;
  }>;
  hasFormatting: boolean;
}

function DocumentPreview({ document, onFileAttachment }: DocumentPreviewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [emailData, setEmailData] = useState<EmailData | null>(null);
  const [wordData, setWordData] = useState<WordData | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    loadPreview();
    
    // Cleanup function to revoke object URLs
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
    };
  }, [document]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    setEmailData(null);
    setWordData(null);
    
    try {
      // For PDFs and images, we can display them directly
      if (document.mime_type.includes('pdf') || document.mime_type.startsWith('image/')) {
        // For PDFs, we might need to handle CORS issues
        if (document.mime_type.includes('pdf')) {
          // Check if the URL needs special handling
          if (document.blob_url.startsWith('http')) {
            // For external URLs, we might need to fetch and create a blob URL
            try {
              const response = await fetch(document.blob_url);
              const blob = await response.blob();
              const url = URL.createObjectURL(blob);
              setObjectUrl(url);
              setPreviewUrl(url);
            } catch (error) {
              console.error('Error fetching PDF:', error);
              // Fallback to direct URL
              setPreviewUrl(document.blob_url);
            }
          } else {
            setPreviewUrl(document.blob_url);
          }
        } else {
          setPreviewUrl(document.blob_url);
        }
      } else if (document.mime_type.includes('text/')) {
        // For text files, fetch and display content
        const response = await fetch(document.blob_url);
        const text = await response.text();
        setPreviewUrl(`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
      } else if (
        document.mime_type === 'application/vnd.ms-outlook' || 
        document.original_filename.toLowerCase().endsWith('.msg') ||
        document.mime_type === 'message/rfc822' ||
        document.original_filename.toLowerCase().endsWith('.eml')
      ) {
        // For email files, parse them server-side
        const fileType = document.original_filename.toLowerCase().endsWith('.eml') ? 'eml' : 'msg';
        const response = await fetch(`/api/parse-msg?url=${encodeURIComponent(document.blob_url)}&type=${fileType}`, {
          method: 'POST'
        });
        if (!response.ok) {
          throw new Error('Failed to parse email');
        }
        const data = await response.json();
        setEmailData(data);
      } else if (
        document.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        document.mime_type === 'application/msword' ||
        document.original_filename.toLowerCase().endsWith('.docx') ||
        document.original_filename.toLowerCase().endsWith('.doc')
      ) {
        // For Word documents, parse them server-side
        const fileType = document.original_filename.toLowerCase().endsWith('.doc') ? 'doc' : 'docx';
        const response = await fetch(`/api/parse-office?url=${encodeURIComponent(document.blob_url)}&type=${fileType}`, {
          method: 'POST'
        });
        if (!response.ok) {
          throw new Error('Failed to parse Word document');
        }
        const data = await response.json();
        setWordData(data);
      } else {
        // For other file types, show a message
        setError('Preview not available for this file type');
      }
    } catch (err) {
      console.error('Error loading preview:', err);
      setError('Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = document.blob_url;
    link.download = document.original_filename;
    link.click();
  };

  const handleAttachmentFile = (attachment: any) => {
    if (onFileAttachment) {
      onFileAttachment(attachment);
    }
  };

  const handleConvertToEml = async () => {
    try {
      const response = await fetch(`/api/convert-msg-to-eml?url=${encodeURIComponent(document.blob_url)}`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to convert file');
      }

      // Get the EML content
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Download the converted file
      const link = document.createElement('a');
      link.href = url;
      link.download = document.original_filename.replace(/\.msg$/i, '.eml');
      link.click();
      
      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error converting MSG to EML:', error);
      alert('Failed to convert MSG to EML');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <AlertCircle className="w-16 h-16 text-gray-400 mb-4" />
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleDownload}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Download className="w-4 h-4 mr-2" />
          Download File
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            <FileText className="w-6 h-6 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-medium text-gray-900 truncate">
                {document.original_filename}
              </h2>
              <p className="text-sm text-gray-500 truncate">
                {document.mime_type} • {new Date(document.upload_date).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {(document.mime_type === 'application/vnd.ms-outlook' || 
              document.original_filename.toLowerCase().endsWith('.msg')) && (
              <button
                onClick={handleConvertToEml}
                className="inline-flex items-center px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                <Mail className="w-4 h-4 mr-2" />
                Convert to .eml
              </button>
            )}
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
          </div>
        </div>
      </div>
      
      {/* Preview Content */}
      <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden min-h-0">
        {document.mime_type.includes('pdf') ? (
          <embed
            src={previewUrl!}
            type="application/pdf"
            className="w-full h-full"
            title="PDF Preview"
          />
        ) : document.mime_type.startsWith('image/') ? (
          <div className="flex items-center justify-center h-full p-4">
            <img
              src={previewUrl!}
              alt={document.original_filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        ) : document.mime_type.includes('text/') ? (
          <iframe
            src={previewUrl!}
            className="w-full h-full"
            title="Text Preview"
            sandbox="allow-same-origin"
          />
        ) : emailData ? (
          <div className="h-full flex flex-col overflow-hidden">
            {/* Email Header */}
            <div className="bg-gray-50 border-b border-gray-200 p-4 flex-shrink-0">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 break-words">{emailData.subject}</h3>
                
                <div className="space-y-1 text-sm">
                  <div className="flex items-start">
                    <User className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-gray-500">From:</span>
                      <span className="ml-2 text-gray-900 break-words">{emailData.from}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Users className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-gray-500">To:</span>
                      <span className="ml-2 text-gray-900 break-words">{emailData.to}</span>
                    </div>
                  </div>
                  
                  {emailData.cc && (
                    <div className="flex items-start">
                      <Users className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-gray-500">Cc:</span>
                        <span className="ml-2 text-gray-900 break-words">{emailData.cc}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start">
                    <Calendar className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <span className="text-gray-500">Date:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(emailData.date).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  
                  {emailData.attachments.length > 0 && (
                    <div className="flex items-start">
                      <Paperclip className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <span className="text-gray-500">Attachments:</span>
                        <span className="ml-2 text-gray-900">
                          {emailData.attachments.length} file{emailData.attachments.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Email Body */}
            <div className="flex-1 overflow-auto p-4">
              {emailData.hasFormatting && emailData.bodyHtml ? (
                <div className="email-content-wrapper">
                  <style dangerouslySetInnerHTML={{ __html: `
                    .email-content-wrapper {
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                      font-size: 14px;
                      line-height: 1.6;
                      color: #333;
                      word-wrap: break-word;
                      overflow-wrap: break-word;
                    }
                    .email-content-wrapper * {
                      max-width: 100% !important;
                    }
                    .email-content-wrapper img {
                      height: auto !important;
                      display: block;
                      margin: 10px 0;
                      max-width: 100% !important;
                    }
                    .email-content-wrapper table {
                      border-collapse: collapse;
                      width: 100%;
                      margin: 10px 0;
                      table-layout: fixed;
                      word-wrap: break-word;
                    }
                    .email-content-wrapper td, .email-content-wrapper th {
                      border: 1px solid #ddd;
                      padding: 8px;
                      text-align: left;
                    }
                    .email-content-wrapper a {
                      color: #0066cc;
                      text-decoration: underline;
                    }
                    .email-content-wrapper p {
                      margin: 0 0 10px 0;
                    }
                    .email-content-wrapper blockquote {
                      margin: 10px 0;
                      padding-left: 15px;
                      border-left: 3px solid #ddd;
                      color: #666;
                    }
                    .email-content-wrapper pre {
                      background: #f5f5f5;
                      padding: 10px;
                      border-radius: 4px;
                      overflow-x: auto;
                      white-space: pre-wrap;
                      word-wrap: break-word;
                    }
                    .email-content-wrapper ul, .email-content-wrapper ol {
                      margin: 10px 0;
                      padding-left: 25px;
                    }
                    .email-content-wrapper h1, .email-content-wrapper h2, .email-content-wrapper h3 {
                      margin: 15px 0 10px 0;
                      font-weight: bold;
                    }
                    .email-content-wrapper h1 { font-size: 24px; }
                    .email-content-wrapper h2 { font-size: 20px; }
                    .email-content-wrapper h3 { font-size: 18px; }
                  ` }} />
                  <div 
                    dangerouslySetInnerHTML={{ __html: emailData.bodyHtml }}
                  />
                </div>
              ) : (
                <div className="whitespace-pre-wrap font-sans text-gray-800 text-sm leading-relaxed">
                  {emailData.bodyText || emailData.body || 'No message content available.'}
                </div>
              )}
              
              {/* Attachments List */}
              {emailData.attachments.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Attachments</h4>
                  <div className="space-y-2">
                    {emailData.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between text-sm text-gray-600 py-2">
                        <div className="flex items-center">
                          <Paperclip className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{attachment.filename}</span>
                          <span className="ml-2 text-gray-400">
                            {attachment.size > 0 ? `(${(attachment.size / 1024).toFixed(1)} KB)` : ''}
                          </span>
                        </div>
                        {attachment.data && !attachment.msgFile && (
                          <button
                            onClick={() => handleAttachmentFile(attachment)}
                            className="ml-4 px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
                          >
                            File Separately
                          </button>
                        )}
                        {attachment.msgFile && (
                          <span className="ml-4 text-xs text-gray-400">
                            (Extract email to access)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : wordData ? (
          <div className="h-full flex flex-col overflow-hidden">
            {/* Word Document Header */}
            <div className="bg-gray-50 border-b border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Word Document</h3>
              </div>
              
              {/* Display any parsing messages */}
              {wordData.messages.length > 0 && (
                <div className="mt-2 space-y-1">
                  {wordData.messages.map((msg, index) => (
                    <div key={index} className={`text-sm ${msg.type === 'error' ? 'text-red-600' : msg.type === 'warning' ? 'text-yellow-600' : 'text-gray-600'}`}>
                      {msg.type === 'warning' && <AlertCircle className="w-4 h-4 inline mr-1" />}
                      {msg.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Word Document Body */}
            <div className="flex-1 overflow-auto p-4">
              {wordData.hasFormatting && wordData.html ? (
                <div className="word-content-wrapper">
                  <style dangerouslySetInnerHTML={{ __html: `
                    .word-content-wrapper {
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                      font-size: 14px;
                      line-height: 1.6;
                      color: #333;
                      word-wrap: break-word;
                      overflow-wrap: break-word;
                    }
                    .word-content-wrapper * {
                      max-width: 100% !important;
                    }
                    .word-content-wrapper img {
                      height: auto !important;
                      display: block;
                      margin: 10px 0;
                      max-width: 100% !important;
                    }
                    .word-content-wrapper table {
                      border-collapse: collapse;
                      width: 100%;
                      margin: 10px 0;
                      table-layout: fixed;
                      word-wrap: break-word;
                    }
                    .word-content-wrapper td, .word-content-wrapper th {
                      border: 1px solid #ddd;
                      padding: 8px;
                      text-align: left;
                    }
                    .word-content-wrapper p {
                      margin: 0 0 10px 0;
                    }
                    .word-content-wrapper ul, .word-content-wrapper ol {
                      margin: 10px 0;
                      padding-left: 25px;
                    }
                    .word-content-wrapper h1, .word-content-wrapper h2, .word-content-wrapper h3 {
                      margin: 15px 0 10px 0;
                      font-weight: bold;
                    }
                    .word-content-wrapper h1 { font-size: 24px; }
                    .word-content-wrapper h2 { font-size: 20px; }
                    .word-content-wrapper h3 { font-size: 18px; }
                  ` }} />
                  <div 
                    dangerouslySetInnerHTML={{ __html: wordData.html }}
                  />
                </div>
              ) : (
                <div className="whitespace-pre-wrap font-sans text-gray-800 text-sm leading-relaxed">
                  {wordData.text}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <FileText className="w-16 h-16 text-gray-400 mb-4" />
            <p className="text-gray-600">Preview not available</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentPreview;